import { analyse, InkEvent, normalize, type StrokeEvent, type HoverEvent } from '@pen/shared';
import { request } from '../api';
import { COLORS, drawPoints, loadBackground, renderAt } from './render';

type Point = StrokeEvent['pts'][number];
type Unsequenced = InkEvent extends infer E ? E extends InkEvent ? Omit<E, 'n'> : never : never;
type Current = { id: string; tool: 'pen' | 'eraser'; t0: number; pts: Point[]; pointer: number; hits: Set<string> };
const key = (id: string) => 'pen-attempt-queue-' + id;
export class Paper {
  readonly ctx: CanvasRenderingContext2D;
  events: InkEvent[] = [];
  queue: InkEvent[] = [];
  w = 700; h = 560;
  tool: 'pen' | 'eraser' = 'pen';
  locked = false;
  cur: Current | null = null;
  private seq = 0;
  private t0 = performance.now();
  private scale = 1;
  private background: HTMLImageElement | null = null;
  private hover: HoverEvent | null = null;
  private hoverTimer: ReturnType<typeof setTimeout> | undefined;
  private flushing: Promise<boolean> | null = null;
  private timer: ReturnType<typeof setInterval>;
  private abort = new AbortController();
  constructor(readonly canvas: HTMLCanvasElement, readonly id: string, private onStatus: (s: string) => void, private onDown: () => void) {
    const ctx = canvas.getContext('2d'); if (!ctx) throw new Error('Canvas is unavailable.'); this.ctx = ctx;
    const signal = this.abort.signal;
    for (const type of ['touchstart', 'touchmove', 'touchend']) canvas.addEventListener(type, e => { if (e.cancelable) e.preventDefault(); }, { passive: false, signal });
    canvas.addEventListener('contextmenu', e => e.preventDefault(), { signal });
    canvas.addEventListener('pointerdown', e => this.down(e), { signal });
    canvas.addEventListener('pointermove', e => this.move(e), { signal });
    for (const type of ['pointerup', 'pointercancel', 'lostpointercapture'] as const) canvas.addEventListener(type, e => this.finish(e), { signal });
    canvas.addEventListener('pointerleave', () => this.endHover(), { signal });
    window.addEventListener('online', () => { void this.flush(); }, { signal });
    window.addEventListener('pagehide', () => { this.endHover(); void this.flush(); }, { signal });
    this.timer = setInterval(() => { void this.flush(); }, 3000);
  }
  async start(events: InkEvent[], background: string | null): Promise<void> {
    this.background = await loadBackground(background);
    // A fresh checkpoint must also drain device backups from earlier visits.
    // Ownership is checked by the server; never discard a queue after a failed upload.
    try {
      for (const savedKey of Object.keys(localStorage)) {
        if (!savedKey.startsWith('pen-attempt-queue-') || savedKey === key(this.id)) continue;
        const savedId = savedKey.slice('pen-attempt-queue-'.length);
        const saved = InkEvent.array().parse(JSON.parse(localStorage.getItem(savedKey) || '[]'));
        try {
          for (let i = 0; i < saved.length; i += 2000) await request('POST', `/api/attempts/${encodeURIComponent(savedId)}/events`, saved.slice(i, i + 2000));
          localStorage.removeItem(savedKey);
        } catch { this.onStatus('Earlier drawing backed up on this device'); }
      }
    } catch { this.onStatus('Local backup unavailable; keep this page open'); }
    try { this.queue = InkEvent.array().parse(JSON.parse(localStorage.getItem(key(this.id)) || '[]')); }
    catch { this.onStatus('Local backup unavailable; keep this page open'); }
    this.events = normalize([...events, ...this.queue]);
    this.seq = this.events.reduce((n, e) => Math.max(n, e.n + 1), 0);
    const a = analyse(this.events); this.w = a.page.w; this.h = a.page.h;
    this.t0 = performance.now() - a.end;
    if (!this.events.some(e => e.k === 'page')) this.emit({ k: 'page', t: 0, w: this.w, h: this.h });
    this.resize(); this.onStatus(this.queue.length ? 'Saving…' : 'Saved'); void this.flush();
  }
  now(): number { return Math.max(0, Math.round(performance.now() - this.t0)); }
  private emit(event: Unsequenced): void {
    const e = { ...event, n: this.seq++ } as InkEvent;
    this.events.push(e); this.queue.push(e); this.persist();
  }
  private persist(): void {
    try { if (this.queue.length) localStorage.setItem(key(this.id), JSON.stringify(this.queue)); else localStorage.removeItem(key(this.id)); }
    catch { this.onStatus('Local backup unavailable; keep this page open'); }
  }
  flush(): Promise<boolean> {
    if (this.flushing) return this.flushing;
    if (!this.queue.length) return Promise.resolve(true);
    const batch = this.queue.slice(0, 2000);
    this.flushing = request('POST', `/api/attempts/${this.id}/events`, batch)
      .then(() => { this.queue.splice(0, batch.length); this.persist(); this.onStatus('Saved'); return true; })
      .catch(() => { this.onStatus('Not saved yet · retrying'); return false; })
      .finally(() => { this.flushing = null; });
    return this.flushing;
  }
  async sync(): Promise<void> {
    this.endHover();
    while (this.queue.length) if (!await this.flush()) throw new Error('Your strokes are backed up on this device. Check the connection and retry.');
  }
  private point(e: PointerEvent, base: number): Point {
    const r = this.canvas.getBoundingClientRect();
    return [Math.round((e.clientX - r.left) * this.w / r.width * 10) / 10, Math.round((e.clientY - r.top) * this.h / r.height * 10) / 10, this.now() - base, e.altitudeAngle ?? 0, e.azimuthAngle ?? 0];
  }
  private down(e: PointerEvent): void {
    if (this.locked || this.cur || !['pen', 'mouse'].includes(e.pointerType) || (e.pointerType === 'mouse' && e.button !== 0)) return;
    e.preventDefault(); this.onDown(); this.endHover();
    const t = this.now(); this.cur = { id: crypto.randomUUID(), tool: this.tool, t0: t, pts: [this.point(e, t)], pointer: e.pointerId, hits: new Set() };
    if (this.tool === 'eraser') this.eraseAt(this.cur.pts[0]!);
    try { this.canvas.setPointerCapture(e.pointerId); } catch { /* Pointer already released. */ }
  }
  private move(e: PointerEvent): void {
    if (this.locked || !['pen', 'mouse'].includes(e.pointerType)) return;
    if (!this.cur) {
      if (e.pointerType === 'pen' && !e.buttons) {
        const t = this.now(); this.hover ??= { k: 'hover', n: 0, t0: t, t1: t, pts: [] };
        const prev = this.hover.pts.at(-1);
        if (!prev || t - this.hover.t0 - prev[2] >= 40) { const p = this.point(e, this.hover.t0); this.hover.pts.push([p[0], p[1], p[2]]); }
        this.hover.t1 = t; clearTimeout(this.hoverTimer); this.hoverTimer = setTimeout(() => this.endHover(), 400);
      }
      return;
    }
    if (e.pointerId !== this.cur.pointer) return;
    const coalesced = e.getCoalescedEvents?.() ?? [];
    for (const c of coalesced.length ? coalesced : [e]) {
      const p = this.point(c, this.cur.t0), prev = this.cur.pts.at(-1)!;
      this.cur.pts.push(p);
      if (this.cur.tool === 'eraser') this.eraseAt(p);
      else { this.ctx.strokeStyle = COLORS.ink; this.ctx.lineWidth = 1.7 / this.scale; drawPoints(this.ctx, [prev, p]); }
    }
  }
  private finish(e: PointerEvent): void {
    const cur = this.cur; if (!cur || cur.pointer !== e.pointerId) return;
    if (cur.tool === 'pen') this.emit({ k: 'stroke', id: cur.id, tool: 'pen', t0: cur.t0, t1: this.now(), pts: cur.pts });
    else if (cur.hits.size) this.emit({ k: 'erase', t: cur.t0, t1: this.now(), ids: [...cur.hits], via: 'eraser' });
    this.cur = null; this.redraw(); void this.flush();
  }
  private eraseAt(p: Point): void {
    const cur = this.cur; if (!cur) return;
    const a = analyse(this.events);
    for (const s of a.strokes) {
      if (a.erasedAt.has(s.id) || cur.hits.has(s.id)) continue;
      const radius = 12 / this.scale;
      let hit = s.pts.some(q => Math.hypot(q[0] - p[0], q[1] - p[1]) < radius);
      for (let i = 1; !hit && i < s.pts.length; i++) {
        const x = s.pts[i - 1]!, y = s.pts[i]!, dx = y[0] - x[0], dy = y[1] - x[1];
        const f = Math.max(0, Math.min(1, ((p[0] - x[0]) * dx + (p[1] - x[1]) * dy) / (dx * dx + dy * dy || 1)));
        hit = Math.hypot(p[0] - x[0] - f * dx, p[1] - x[1] - f * dy) < radius;
      }
      if (hit) { cur.hits.add(s.id); this.ctx.strokeStyle = '#e0452b88'; this.ctx.lineWidth = 4 / this.scale; drawPoints(this.ctx, s.pts); }
    }
  }
  private endHover(): void { clearTimeout(this.hoverTimer); if (this.hover?.pts.length) { const { n: _n, ...event } = this.hover; this.emit(event); } this.hover = null; }
  setDisplayScale(scale: number): void { if (scale > 0 && Math.abs(scale - this.scale) > .005 && !this.cur) { this.scale = scale; this.redraw(); } }
  private resize(): void {
    const d = devicePixelRatio || 1; this.canvas.width = Math.round(this.w * d); this.canvas.height = Math.round(this.h * d);
    this.canvas.style.width = this.w + 'px'; this.canvas.style.height = this.h + 'px'; this.ctx.setTransform(d, 0, 0, d, 0, 0); this.redraw();
  }
  redraw(): void { renderAt(this.ctx, analyse(this.events), Infinity, 'ink', this.background, 560, 1.7 / this.scale); }
  grow(): boolean {
    if (this.locked || this.cur || this.h >= 2240) return false;
    this.endHover(); this.h += 560; this.emit({ k: 'page', t: this.now(), w: this.w, h: this.h }); this.resize(); void this.flush(); return true;
  }
  undo(): void {
    if (this.locked || this.cur) return;
    const a = analyse(this.events), s = a.strokes.findLast(s => !a.erasedAt.has(s.id));
    if (s) { this.emit({ k: 'erase', t: this.now(), ids: [s.id], via: 'undo' }); this.redraw(); void this.flush(); }
  }
  say(text: string, start: number, end = this.now()): void {
    this.emit({ k: 'say', t: Math.max(0, Math.round(start)), t1: Math.max(Math.round(start), Math.round(end)), text: text.slice(0, 4000) });
    void this.flush();
  }
  end(): void { this.endHover(); this.emit({ k: 'end', t: this.now() }); }
  exportPNGs(): { ink: string; process: string } {
    if (this.cur) throw new Error('Lift the Pencil before checking.');
    const canvas = document.createElement('canvas'); canvas.width = this.w; canvas.height = this.h;
    const ctx = canvas.getContext('2d'); if (!ctx) throw new Error('Canvas is unavailable.');
    const a = analyse(this.events); renderAt(ctx, a, Infinity, 'ink', this.background); const ink = canvas.toDataURL('image/png');
    renderAt(ctx, a, Infinity, 'process', this.background); return { ink, process: canvas.toDataURL('image/png') };
  }
  dispose(): void { if (this.cur) this.finish({ pointerId: this.cur.pointer } as PointerEvent); this.endHover(); void this.flush(); clearInterval(this.timer); clearTimeout(this.hoverTimer); this.abort.abort(); }
}
